/**
 * Knowledge-base updates admin tab.
 *
 * QRAM is installed without internet access, so the knowledge base (vendor and device positions, regulations and
 * their dates, policies, the cryptography catalogue) and the evidence behind it arrive as a bundle file:
 *   - Apply an update: upload a bundle, see what it would change, apply it. Every bundle applied is listed.
 *   - Publish an update: the install that maintains the knowledge base downloads it as a bundle (with an integrity
 *     hash, so a file changed on the way is refused).
 *   - Evidence: the passage behind each KB row, its source and when it was checked; on the maintaining install,
 *     capture fetches every source again, and a reviewer can quote a source the capture cannot read.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUrl, ApiError } from '../api/client';
import { useAdminReadOnly } from '../lib/admin-read-only';

interface TableDiff { table: string; added: number; changed: number; unchanged: number; ignoredColumns: string[]; samples: Array<{ key: string; change: 'added' | 'changed'; columns: string[] }> }
interface Preview {
  check: { ok: boolean; problems: string[] };
  bundle: { bundle_id: string; label: string | null; created_at: string; source_build: string | null; already_applied: string | null };
  tables: TableDiff[]; skippedTables: string[]; totals: { added: number; changed: number; unchanged: number };
}
interface Status {
  imports: Array<{ id: string; label: string | null; bundle_created_at: string; sha256: string; summary: Array<{ table: string; added: number; changed: number }>; applied_by: string | null; applied_at: string }>;
  evidenceCapture: boolean; schemaVersion: number;
  capture: { running: boolean; startedAt: string | null; finishedAt: string | null; total: number; done: number; failed: number; errors: Array<{ url: string; error: string }> };
}
interface Coverage { table: string; rows_with_sources: number; rows_with_evidence: number; sources: number; captured: number; curated: number; searches: number; failed: number; secondary: number }
interface Evidence { id: string; kb_table: string; kb_row_id: string; row_label: string | null; source_url: string | null; title: string | null; publisher: string | null; source_type: string; excerpt: string | null; excerpt_kind: string; published_on: string | null; checked_on: string; capture_error: string | null }

const TABLE_LABEL: Record<string, string> = {
  kb_vendor_pqc: 'Vendor positions', kb_device_pqc: 'Device rules', regulatory_milestone: 'Regulatory dates', kb_regulation_requirement: 'Regulation requirements',
  kb_policy: 'Policies', kb_policy_rule: 'Policy rules', kb_policy_date_source: 'Policy date sources', kb_regulatory_level: 'Regulatory levels',
  kb_device_capacity: 'Device capacity', crqc_probability: 'CRQC curve', kb_evidence: 'Evidence', kb_version: 'Catalogue versions',
};
const label = (t: string) => TABLE_LABEL[t] ?? t.replace(/^kb_/, '').replace(/_/g, ' ');
const TYPE_LABEL: Record<string, string> = { vendor: 'Vendor', regulator: 'Regulator', standards_body: 'Standard', secondary: 'Secondary', search: 'Search (none found)' };
const btn = 'px-3 py-1 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50';
const errText = (e: unknown) => (e instanceof ApiError ? [e.message, ...(((e.body as { error?: { details?: { problems?: string[] } } })?.error?.details?.problems) ?? [])].join(' ') : String(e));

export function KbUpdatesAdminTab() {
  const readOnly = useAdminReadOnly();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['kb-bundle-status'],
    queryFn: () => api.get<Status>('/admin/kb-bundle/status'),
    refetchInterval: (q) => (q.state.data?.capture.running ? 3000 : false),
  });

  return (
    <div className="space-y-8">
      <p className="text-sm text-ink-700 max-w-3xl">
        The knowledge base holds what the assessment relies on: vendor and device post-quantum positions, regulations and their dates, policies and the
        cryptography catalogue, each with its source and the passage the source says. It is updated from a bundle file, so an install without
        internet access stays current. A bundle adds and updates rows; it never deletes them, and it never touches entries your organisation added.
      </p>
      <ApplySection readOnly={readOnly} onApplied={() => { void qc.invalidateQueries({ queryKey: ['kb-bundle-status'] }); void qc.invalidateQueries({ queryKey: ['kb-evidence'] }); }} />
      <HistorySection status={status.data} />
      <PublishSection />
      <EvidenceSection status={status.data} readOnly={readOnly} onCapture={() => void qc.invalidateQueries({ queryKey: ['kb-bundle-status'] })} />
    </div>
  );
}

function ApplySection({ readOnly, onApplied }: { readOnly: boolean; onApplied: () => void }) {
  const [bundle, setBundle] = useState<unknown>(null);
  const [fileName, setFileName] = useState('');
  const [readError, setReadError] = useState<string | null>(null);
  const [confirmOrigin, setConfirmOrigin] = useState(false);
  const preview = useMutation({ mutationFn: (b: unknown) => api.post<Preview>('/admin/kb-bundle/preview', b) });
  const apply = useMutation({
    mutationFn: () => api.post<{ totals: Preview['totals'] }>('/admin/kb-bundle/apply', { bundle, confirm_origin: confirmOrigin }),
    onSuccess: onApplied,
  });

  const onFile = async (f: File | undefined) => {
    setReadError(null); setBundle(null); preview.reset(); apply.reset(); setConfirmOrigin(false);
    if (!f) return;
    setFileName(f.name);
    try {
      const parsed: unknown = JSON.parse(await f.text());
      setBundle(parsed);
      preview.mutate(parsed);
    } catch {
      setReadError('This file is not a knowledge-base bundle (it is not readable JSON).');
    }
  };

  const p = preview.data;
  const changedTables = (p?.tables ?? []).filter((t) => t.added + t.changed > 0);
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink-900 mb-2">Apply an update</h3>
      {readOnly ? <p className="text-xs text-ink-500">Only a super user can apply an update.</p> : (
        <label className="text-xs text-ink-500">
          Bundle file (.json)
          <input type="file" accept=".json,application/json" className="block mt-1 text-sm" data-testid="kb-bundle-file" onChange={(e) => void onFile(e.target.files?.[0])} />
        </label>
      )}
      {readError && <p className="mt-2 text-sm text-red-700">{readError}</p>}
      {preview.isPending && <p className="mt-2 text-sm text-ink-500 italic">Checking {fileName}…</p>}
      {preview.isError && <p className="mt-2 text-sm text-red-700">{errText(preview.error)}</p>}
      {p && (
        <div className="mt-3 border border-ink-200 rounded p-3 text-sm space-y-2" data-testid="kb-bundle-preview">
          <div>
            <span className="font-medium">{p.bundle.label ?? 'Unlabelled bundle'}</span>
            <span className="text-ink-500"> · made {p.bundle.created_at ? new Date(p.bundle.created_at).toLocaleString() : 'unknown'}</span>
            {p.bundle.source_build && <span className="text-ink-500"> · build {p.bundle.source_build}</span>}
          </div>
          {!p.check.ok ? (
            <ul className="list-disc ml-5 text-red-700">{p.check.problems.map((m) => <li key={m}>{m}</li>)}</ul>
          ) : (
            <>
              <div className="text-green-800">The file is intact (its integrity hash matches).</div>
              {p.bundle.already_applied && <div className="text-ink-700">This bundle was already applied on {new Date(p.bundle.already_applied).toLocaleString()}.</div>}
              <div>
                {p.totals.added} rows to add, {p.totals.changed} to update, {p.totals.unchanged} already current.
                {p.skippedTables.length > 0 && <span className="text-ink-500"> Not taken by this version: {p.skippedTables.join(', ')}.</span>}
              </div>
              {changedTables.length > 0 && (
                <div className="border border-ink-200 rounded overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
                      <tr><th className="px-2 py-2 text-left">Table</th><th className="px-2 py-2 text-right">Add</th><th className="px-2 py-2 text-right">Update</th><th className="px-2 py-2 text-left">Fields that change (first rows)</th></tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                      {changedTables.map((t) => (
                        <tr key={t.table}>
                          <td className="px-2 py-1">{label(t.table)}</td>
                          <td className="px-2 py-1 text-right font-mono">{t.added}</td>
                          <td className="px-2 py-1 text-right font-mono">{t.changed}</td>
                          <td className="px-2 py-1 text-ink-600">{[...new Set(t.samples.flatMap((s) => s.columns))].join(', ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={confirmOrigin} onChange={(e) => setConfirmOrigin(e.target.checked)} data-testid="kb-confirm-origin" />
                This bundle came from AvinyaSQ, through our approved transfer route.
              </label>
              <button className={btn} disabled={apply.isPending || apply.isSuccess || !confirmOrigin || p.totals.added + p.totals.changed === 0}
                onClick={() => apply.mutate()} data-testid="kb-apply">
                {apply.isPending ? 'Applying…' : 'Apply update'}
              </button>
              {apply.isSuccess && <p className="text-green-800">Applied: {apply.data.totals.added} added, {apply.data.totals.changed} updated. Assessments run from now on use it; re-run an assessment to update its reports.</p>}
              {apply.isError && <p className="text-red-700">{errText(apply.error)}</p>}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function HistorySection({ status }: { status: Status | undefined }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink-900 mb-2">Updates applied</h3>
      {!status ? <p className="text-sm text-ink-500 italic">Loading…</p> : status.imports.length === 0 ? (
        <p className="text-sm text-ink-500">None yet. The knowledge base is the one shipped with this version of QRAM (database version {status.schemaVersion}).</p>
      ) : (
        <div className="border border-ink-200 rounded overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
              <tr><th className="px-2 py-2 text-left">Applied</th><th className="px-2 py-2 text-left">Bundle</th><th className="px-2 py-2 text-left">Made</th><th className="px-2 py-2 text-left">File hash</th><th className="px-2 py-2 text-left">Changes</th><th className="px-2 py-2 text-left">By</th></tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {status.imports.map((i) => (
                <tr key={i.id}>
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(i.applied_at).toLocaleString()}</td>
                  <td className="px-2 py-1">{i.label ?? '—'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(i.bundle_created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-1 font-mono">{i.sha256.slice(0, 12)}</td>
                  <td className="px-2 py-1">{i.summary.filter((s) => s.added + s.changed > 0).map((s) => `${label(s.table)} +${s.added}/~${s.changed}`).join('; ') || 'nothing changed'}</td>
                  <td className="px-2 py-1">{i.applied_by ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PublishSection() {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(apiUrl(`/admin/kb-bundle/export?label=${encodeURIComponent(name)}`), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const file = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'QRAM_knowledge_base.json';
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = file; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`The download failed: ${String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink-900 mb-2">Publish an update</h3>
      <p className="text-xs text-ink-500 mb-2 max-w-3xl">
        Downloads this install's knowledge base and its evidence as a bundle, to apply on other installs. The file carries
        an integrity hash: a copy changed on the way is refused.
      </p>
      <div className="flex items-end gap-2">
        <label className="text-xs text-ink-500">
          Label
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. November 2026 review" className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm w-72" />
        </label>
        <button className={btn} disabled={busy} onClick={() => void download()} data-testid="kb-export">{busy ? 'Preparing…' : 'Download bundle'}</button>
      </div>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
    </section>
  );
}

function EvidenceSection({ status, readOnly, onCapture }: { status: Status | undefined; readOnly: boolean; onCapture: () => void }) {
  const [table, setTable] = useState('');
  const [q, setQ] = useState('');
  const [problems, setProblems] = useState(false);
  const params = useMemo(() => new URLSearchParams({ ...(table ? { table } : {}), ...(q ? { q } : {}), ...(problems ? { problems: '1' } : {}) }).toString(), [table, q, problems]);
  const evidence = useQuery({ queryKey: ['kb-evidence', params, status?.capture.finishedAt], queryFn: () => api.get<{ rows: Evidence[]; coverage: Coverage[] }>(`/admin/kb-bundle/evidence?${params}`) });
  const capture = useMutation({ mutationFn: () => api.post('/admin/kb-bundle/evidence/capture'), onSuccess: onCapture });
  const c = status?.capture;
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink-900 mb-2">Evidence</h3>
      <p className="text-xs text-ink-500 mb-2 max-w-3xl">
        The passage each source gives for a knowledge-base entry, kept here so it can be checked without internet access. "Curated" passages were
        quoted by a reviewer; "captured" ones were taken from the source page on the date shown.
      </p>
      {evidence.data && (
        <div className="border border-ink-200 rounded overflow-x-auto mb-3">
          <table className="min-w-full text-xs">
            <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
              <tr><th className="px-2 py-2 text-left">Table</th><th className="px-2 py-2 text-right">Entries with sources</th><th className="px-2 py-2 text-right">With evidence</th><th className="px-2 py-2 text-right">Curated</th><th className="px-2 py-2 text-right">Captured</th><th className="px-2 py-2 text-right">Searches</th><th className="px-2 py-2 text-right">Unreachable</th><th className="px-2 py-2 text-right">Secondary</th></tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {evidence.data.coverage.map((r) => (
                <tr key={r.table}>
                  <td className="px-2 py-1">{label(r.table)}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.rows_with_sources}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.rows_with_evidence}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.curated}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.captured}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.searches}</td>
                  <td className={`px-2 py-1 text-right font-mono ${r.failed ? 'text-red-700' : ''}`}>{r.failed}</td>
                  <td className={`px-2 py-1 text-right font-mono ${r.secondary ? 'text-amber-800' : ''}`}>{r.secondary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {status?.evidenceCapture && !readOnly && (
        <div className="mb-3 text-sm">
          <button className={btn} disabled={c?.running || capture.isPending} onClick={() => capture.mutate()} data-testid="kb-capture">
            {c?.running ? `Capturing… ${c.done} of ${c.total}` : 'Capture all sources now'}
          </button>
          {!c?.running && c?.finishedAt && <span className="ml-2 text-ink-500">Last capture {new Date(c.finishedAt).toLocaleString()}: {c.total} sources, {c.failed} unreachable.</span>}
          {capture.isError && <span className="ml-2 text-red-700">{errText(capture.error)}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3 mb-2">
        <label className="text-xs text-ink-500">Table
          <select value={table} onChange={(e) => setTable(e.target.value)} className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm">
            <option value="">All</option>
            {['kb_vendor_pqc', 'kb_device_pqc', 'regulatory_milestone', 'kb_regulation_requirement'].map((t) => <option key={t} value={t}>{label(t)}</option>)}
          </select>
        </label>
        <label className="text-xs text-ink-500">Search
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="vendor, product, regulation or text" className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm w-64" />
        </label>
        <label className="flex items-center gap-1 text-xs text-ink-700"><input type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} /> Only gaps (unreachable, secondary, no passage)</label>
      </div>
      {evidence.isLoading ? <p className="text-sm text-ink-500 italic">Loading…</p> : evidence.isError ? <p className="text-sm text-red-700">Failed to load the evidence.</p> : (
        <div className="border border-ink-200 rounded divide-y divide-ink-100" data-testid="kb-evidence-list">
          {(evidence.data?.rows ?? []).length === 0 && <p className="p-3 text-sm text-ink-500">No evidence matches.</p>}
          {(evidence.data?.rows ?? []).map((e) => (
            <div key={e.id} className="p-2 text-xs">
              <div className="flex flex-wrap gap-x-2">
                <span className="font-medium text-ink-900">{e.row_label ?? e.kb_row_id}</span>
                <span className="text-ink-500">{label(e.kb_table)}</span>
                <span className={e.source_type === 'secondary' ? 'text-amber-800' : 'text-ink-600'}>{TYPE_LABEL[e.source_type] ?? e.source_type}</span>
                <span className="text-ink-500">{e.excerpt_kind}, checked {e.checked_on}{e.published_on ? `, published ${e.published_on}` : ''}</span>
              </div>
              {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer noopener" className="text-accent hover:underline break-all">{e.title ?? e.source_url}</a>}
              {e.excerpt ? <p className="mt-1 text-ink-700 whitespace-pre-line">{e.excerpt}</p> : <p className="mt-1 text-ink-400 italic">No passage stored.</p>}
              {e.capture_error && <p className="mt-1 text-red-700">Last capture failed: {e.capture_error}</p>}
              {!readOnly && <QuoteEditor row={e} />}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** A reviewer's quote for a source the capture cannot read; saved as curated, which a later capture never overwrites. */
function QuoteEditor({ row }: { row: Evidence }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'passage' | 'none_found'>(row.source_type === 'search' ? 'none_found' : 'passage');
  const [excerpt, setExcerpt] = useState(row.excerpt_kind === 'curated' ? row.excerpt ?? '' : '');
  const [title, setTitle] = useState(row.title ?? '');
  const [publishedOn, setPublishedOn] = useState(row.published_on ?? '');
  const save = useMutation({
    mutationFn: () => api.put<Evidence>(`/admin/kb-bundle/evidence/${row.id}/quote`, { excerpt, kind, title: title || null, published_on: publishedOn || null }),
    onSuccess: () => { setOpen(false); void qc.invalidateQueries({ queryKey: ['kb-evidence'] }); },
  });
  if (!open) {
    return (
      <button className="mt-1 text-accent hover:underline" onClick={() => setOpen(true)} data-testid={`kb-quote-${row.id}`}>
        {row.excerpt_kind === 'curated' && row.excerpt ? 'Edit quote' : 'Add quote'}
      </button>
    );
  }
  return (
    <div className="mt-2 p-2 border border-ink-200 rounded bg-ink-50 space-y-2 max-w-3xl">
      <div className="flex gap-4 text-ink-700">
        <label className="flex items-center gap-1"><input type="radio" checked={kind === 'passage'} onChange={() => setKind('passage')} /> Passage from the source</label>
        <label className="flex items-center gap-1"><input type="radio" checked={kind === 'none_found'} onChange={() => setKind('none_found')} /> No statement found</label>
      </div>
      <textarea value={excerpt} onChange={(ev) => setExcerpt(ev.target.value)} rows={4} maxLength={4000}
        placeholder={kind === 'passage' ? 'Copy the passage exactly as the source gives it. Mark cuts with […].' : 'What was searched, where and up to which release or date, e.g. "Release notes up to 10.6(4)M searched for ML-KEM and post-quantum: no match."'}
        className="block w-full px-2 py-1 border border-ink-200 rounded text-sm bg-white" data-testid="kb-quote-text" />
      <div className="flex flex-wrap gap-3">
        <label className="text-ink-500">Page title
          <input value={title} onChange={(ev) => setTitle(ev.target.value)} maxLength={512} className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm w-80 bg-white" />
        </label>
        <label className="text-ink-500">Published on
          <input type="date" value={publishedOn} onChange={(ev) => setPublishedOn(ev.target.value)} className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm bg-white" />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button className={btn} disabled={excerpt.trim().length < 10 || save.isPending} onClick={() => save.mutate()} data-testid="kb-quote-save">Save quote</button>
        <button className="px-3 py-1 text-sm text-ink-700 hover:underline" onClick={() => setOpen(false)}>Cancel</button>
        {save.isError && <span className="text-red-700">{errText(save.error)}</span>}
      </div>
      <p className="text-ink-500">Saved as curated and checked today. A later capture never overwrites it, and it travels in the next knowledge-base bundle.</p>
    </div>
  );
}
